import { API_BASE_URL } from './httpClient';
import { isIOS } from './iosAudioHelper';

// CRITICAL: Use a single global Audio object for iOS Safari compatibility
// Creating new Audio objects after the first one can fail on iOS
let currentAudio = null;
let globalBreathingAudio = null;
let isAudioInitialized = false;

/**
 * Get or create the global breathing audio object
 * On iOS, we must reuse the same Audio object that was unlocked by user gesture
 */
const getBreathingAudio = () => {
  if (!globalBreathingAudio) {
    globalBreathingAudio = new Audio();
    globalBreathingAudio.preload = 'auto';
    console.log('[BreathingVoice] 🎵 Created global breathing audio object');
  }
  return globalBreathingAudio;
};

/**
 * Initialize/unlock the breathing voice audio on user gesture
 * MUST be called synchronously within a user gesture handler (click, touchend)
 * This "unlocks" the audio object for subsequent programmatic plays
 */
export const initBreathingVoice = async () => {
  if (isAudioInitialized && !isIOS()) {
    console.log('[BreathingVoice] Already initialized');
    return true;
  }
  
  console.log('[BreathingVoice] 🔓 Initializing audio on user gesture...');
  
  const audio = getBreathingAudio();
  
  try {
    // Play a tiny bit of silence to "unlock" the audio object
    // This is a common iOS Safari workaround
    audio.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAAYYoRwmHAAAAAAD/+1DEAAAGAAGn9AAAIAAANIAAAAQAAAGkAAAAIAAAAaQAAAAgAAA0gAAAAEludHJvAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQxAADwAABpAAAACAAADSAAAAETEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVU=';
    audio.volume = 0.01;
    
    await audio.play();
    audio.pause();
    audio.currentTime = 0;
    
    isAudioInitialized = true;
    console.log('[BreathingVoice] ✅ Audio initialized/unlocked successfully');
    return true;
  } catch (error) {
    console.error('[BreathingVoice] ❌ Failed to initialize audio:', error);
    return false;
  }
};

/**
 * Map breathing phase to pre-generated audio file WITH SPECIFIC DURATION
 * Uses ElevenLabs Hera voice - files are pre-generated and stored on server
 * @param {string} text - Breathing instruction text
 * @param {string} lang - Language (fr or en)
 * @param {number} duration - Duration in seconds for this phase
 * @returns {string} - Filename for the pre-generated audio
 */
const getBreathingAudioFile = (text, lang, duration) => {
  if (!duration || duration === 0) {
    return null; // No audio for 0-duration phases
  }
  
  const lowerText = text.toLowerCase();
  let phase = null;
  
  // Detect phase from text
  if (lowerText.includes('inspir') || lowerText.includes('breathe in')) {
    phase = 'inhale';
  } else if (lowerText.includes('reten') || lowerText.includes('hold')) {
    // For hold phases, we need to determine if it's hold1 or hold2
    // Based on the breathing cycle order: inhale -> hold1 -> exhale -> hold2
    phase = 'hold1'; // Default, will be overridden for hold2 in caller
  } else if (lowerText.includes('expir') || lowerText.includes('breathe out')) {
    phase = 'exhale';
  }
  
  if (!phase) {
    console.warn(`[BreathingVoice] Unknown phase for text: "${text}"`);
    return null;
  }
  
  // Build filename with duration
  const filename = `breathing_${phase}_${duration}s_${lang}.mp3`;
  return filename;
};

/**
 * Play voice instruction for guided breathing
 * Uses PRE-GENERATED audio files with Hera voice (ElevenLabs)
 * V1.6: Simplified iOS Safari fix - immediate play() after src change
 * @param {Object} options
 * @param {string} options.text - Text to convert to speech
 * @param {string} options.lang - Language (fr or en)
 * @param {number} options.duration - Duration in seconds for this phase
 * @param {number} options.volume - Volume (0-1)
 * @param {boolean} options.voiceEnabled - Whether voice is enabled
 * @param {string} options.phase - Optional: explicit phase name (inhale, hold1, exhale, hold2)
 */
export const playVoiceInstruction = async ({ text, lang, duration, volume = 0.8, voiceEnabled, phase }) => {
  if (!voiceEnabled || !text) {
    return;
  }

  try {
    // Stop any currently playing voice (but keep the audio object)
    stopVoiceInstruction();

    // If phase is explicitly provided, use it
    let audioPhase = phase;
    
    if (!audioPhase) {
      // Detect phase from text
      const lowerText = text.toLowerCase();
      if (lowerText.includes('inspir') || lowerText.includes('breathe in')) {
        audioPhase = 'inhale';
      } else if (lowerText.includes('reten') || lowerText.includes('hold')) {
        audioPhase = 'hold1'; // Default
      } else if (lowerText.includes('expir') || lowerText.includes('breathe out')) {
        audioPhase = 'exhale';
      }
    }
    
    if (!audioPhase || !duration || duration === 0) {
      console.log(`[BreathingVoice] Skipping - no phase or duration: phase=${audioPhase}, duration=${duration}`);
      return;
    }

    // Build filename
    const filename = `breathing_${audioPhase}_${duration}s_${lang}.mp3`;
    const R2_AUDIO_BASE_URL =
      'https://pub-dab66ff921a842e5b7fc99c092357640.r2.dev';

    const url =
      `${R2_AUDIO_BASE_URL}/voice/breathing/${encodeURIComponent(filename)}`;
      console.log('[BreathingVoice] URL:', url);
    
    console.log(`[BreathingVoice] Playing: ${filename} (${lang}, vol: ${Math.round(volume * 100)}%)`);
    
    // V1.6: Reuse the global audio object for iOS Safari compatibility
    if (currentAudio) {
      try {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      } catch (e) {}
    }

    const audio = new Audio(url);
    audio.preload = 'auto';
    audio.volume = volume;
    currentAudio = audio;

    audio.onerror = (e) => {
      console.error(`[BreathingVoice] Error loading: ${filename}`, e);
    };

    const playPromise = audio.play();
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log(`[BreathingVoice] ✅ Playing: ${filename}`);
        })
        .catch((error) => {
          console.error(`[BreathingVoice] ❌ Play failed: ${error.name} - ${error.message}`);
        });
    }
    
    // Clean up reference when finished (but keep the global object)
    audio.onended = () => {
      console.log(`[BreathingVoice] ✅ Finished: ${filename}`);
      currentAudio = null;
    };
    
  } catch (error) {
    console.error('[BreathingVoice] Error:', error);
    currentAudio = null;
  }
};

/**
 * Stop any currently playing voice instruction
 * V1.5: Stops playback but keeps the global audio object for reuse
 */
export const stopVoiceInstruction = () => {
  if (currentAudio) {
    try {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    } catch (error) {
      console.error('[BreathingVoice] Error stopping:', error);
    }
    currentAudio = null;
  }
  // Note: We keep globalBreathingAudio alive for iOS reuse
};
